import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CapaMapa, ListadoItem } from "../../../interfaces/Indicator";
import { ISelectedItem } from "../../../components/Inputs/Selects/MultiSelectCheck";
import { CustomSelectsOption } from "../../../components/Inputs/Selects/CustomSelect";
import { isLayerActiveForYear } from "../utils/helpers";
import { ChartTypeValue } from "../../../utils/chartTypes";
import { isMapGraph } from "../components/Graphics/Filters/helpers";

export const useMapLayersSelect = (mapLayer: CapaMapa[], type_of_graph: ChartTypeValue, chartId: number | string) => {
    const [activeLayersIds, setActiveLayersIds] = useState<number[]>([]);
    const [year, setYear] = useState<string | null>(null);
    const prevChartIdRef = useRef<number | string>(chartId);

    const layersWithVisibleItems: CapaMapa[] = useMemo(() => {
        return (mapLayer ?? []).map((capa) => ({
            ...capa,
            items: capa.items.filter((item: ListadoItem) => isLayerActiveForYear(item, year)),
        }));
    }, [mapLayer, year]);

    const enabledLayerIds = useMemo(
        () => new Set(layersWithVisibleItems.filter((c) => c.items.length > 0).map((c) => c.id)),
        [layersWithVisibleItems]
    );

    useEffect(() => {
        const isNewChart = prevChartIdRef.current !== chartId;
        prevChartIdRef.current = chartId;

        setActiveLayersIds((prev) =>
            isNewChart
                ? []
                : prev.filter((id) => enabledLayerIds.has(id))
        );
    }, [chartId, enabledLayerIds]);

    const handleSelectLayer = useCallback((selected: ISelectedItem) => {
        if (selected.id === undefined) return;
        if (!enabledLayerIds.has(selected.id)) return;

        setActiveLayersIds((prev) =>
            prev.includes(selected.id!) ? prev.filter((id) => id !== selected.id) : [...prev, selected.id!]
        );
    }, [enabledLayerIds]);

    const options: CustomSelectsOption[] = (mapLayer ?? []).map((l) => ({
        value: l.nombre,
        id: l.id,
        label: l.nombre,
        disabled: !enabledLayerIds.has(l.id),
    }));

    const selectedItems: ISelectedItem[] = (mapLayer ?? [])
        .filter((l) => activeLayersIds.includes(l.id))
        .map((l) => ({ label: "Capas del mapa", value: l.nombre, id: l.id }));

    return {
        activeLayersIds,
        options,
        selectedItems,
        onSelect: handleSelectLayer,
        layersWithVisibleItems,
        setYear,
        showSelectorIcon: enabledLayerIds.size > 0 && isMapGraph(type_of_graph)
    };
}